import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { researchBySlug } from "@/content/en/research";
import type { ResearchItem } from "@/content/en/types";
import { conceptBySlug } from "@/content/en/concepts";
import { ideaBySlug } from "@/content/en/ideas";
import { BriefingCTA, TopicList } from "@/components/en/EnPrimitives";
import {
  breadcrumbSchema,
  enHreflangLinks,
  EN_HOME,
  SITE_URL,
} from "@/lib/en-schema";
import { truncate } from "@/lib/en-meta";
import { pageTitle } from "@/lib/brand";
import { listPublishedReports, type ReportRow } from "@/lib/reports.functions";
import { findReportBySlug, reportSlugMap } from "@/lib/report-slug";

const TR_ORIGIN = "https://akillisurdurulebilirlik.com";

type LibraryData = {
  item: null;
  report: ReportRow;
  slug: string;
  related: Array<{ title: string; organization: string; year: number | null; slug: string }>;
};

function libraryHead(d: LibraryData) {
  const { report, slug } = d;
  const canonical = `${SITE_URL}/research/${slug}`;
  const trUrl = `${TR_ORIGIN}/raporlar/${slug}`;
  const org = report.organization || "Report";
  const title = pageTitle(`${report.title}${report.year ? ` (${report.year})` : ""} — ${org}`, "en");
  const description = truncate(
    report.summary_en || `Summary and source link for "${report.title}" by ${org}.`,
  );
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: canonical },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "tr_TR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: canonical }, ...enHreflangLinks(canonical, trUrl)],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Report",
          name: report.title,
          url: canonical,
          inLanguage: "en",
          ...(report.year ? { datePublished: String(report.year) } : {}),
          ...(report.organization ? { publisher: { "@type": "Organization", name: report.organization } } : {}),
          ...(report.summary_en ? { abstract: report.summary_en } : {}),
          ...(report.topics?.length ? { keywords: report.topics.join(", ") } : {}),
          sameAs: report.url,
          isAccessibleForFree: true,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbSchema([
            { name: "Home", item: EN_HOME },
            { name: "Source Notes", item: `${SITE_URL}/research` },
            { name: report.title, item: canonical },
          ]),
        ),
      },
    ],
  };
}

export const Route = createFileRoute("/research/$slug")({
  loader: async ({ params, context }): Promise<{ item: ResearchItem; report: null } | LibraryData> => {
    const item = researchBySlug(params.slug);
    if (item) return { item, report: null };
    // Every report-library entry also gets its own English page, sharing the
    // slug with its Turkish twin at /raporlar/{slug}.
    const list = ((await context.queryClient.ensureQueryData({
      queryKey: ["public-reports"],
      queryFn: () => listPublishedReports(),
    })) ?? []) as ReportRow[];
    const report = findReportBySlug(list, params.slug);
    if (!report) throw notFound();
    const slugs = reportSlugMap(list);
    const related = list
      .filter((r) => r.id !== report.id && r.category === report.category)
      .slice(0, 4)
      .map((r) => ({ title: r.title, organization: r.organization, year: r.year, slug: slugs.get(r.id)! }));
    return { item: null, report, slug: slugs.get(report.id)!, related };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: pageTitle("Research not found", "en") }, { name: "robots", content: "noindex" }] };
    }
    if (loaderData.report) return libraryHead(loaderData);
    const r = loaderData.item;
    const canonical = `https://smartsustainability.ai/research/${params.slug}`;
    return {
      meta: [
        { title: `${r.title} — ${r.institution} · Research` },
        { name: "description", content: truncate(r.whyItMatters) },
        { property: "og:title", content: `${r.title} — ${r.institution}` },
        { property: "og:description", content: truncate(r.whyItMatters) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "tr_TR" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${r.title} — ${r.institution}` },
        { name: "twitter:description", content: truncate(r.whyItMatters) },
      ],
      links: [
        { rel: "canonical", href: canonical },
        ...enHreflangLinks(canonical),
      ],

      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Report",
            name: r.title,
            about: r.topics.join(", "),
            datePublished: `${r.year}-01-01`,
            publisher: { "@type": "Organization", name: r.institution },
            url: r.sourceUrl,
            inLanguage: "en",
            mainEntityOfPage: canonical,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbSchema([
              { name: "Home", item: EN_HOME },
              { name: "Source Notes", item: `${SITE_URL}/research` },
              { name: r.title, item: canonical },
            ]),
          ),
        },
      ],
    };
  },

  component: ResearchDetail,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-20 text-center">
      <h1 className="font-serif text-3xl text-ink">Research not found</h1>
      <Link to="/research" className="mt-6 inline-block text-[11px] uppercase tracking-[0.2em] text-clay underline underline-offset-4">
        Back to Research
      </Link>
    </div>
  ),
});

function ResearchDetail() {
  const data = Route.useLoaderData();
  if (data.report) return <LibraryReport data={data} />;
  const r = data.item;
  return (
    <article className="mx-auto max-w-3xl px-6 py-16 md:py-20">
      <header className="mb-10">
        <p className="text-[10px] uppercase tracking-[0.24em] text-clay">Research</p>
        <h1 className="mt-4 font-serif text-3xl leading-tight text-ink md:text-4xl">{r.title}</h1>
        <p className="mt-3 text-[13px] uppercase tracking-[0.14em] text-muted-foreground">
          {r.institution} · {r.year}
        </p>
      </header>

      <section className="mb-8">
        <h2 className="mb-3 font-serif text-xl text-ink">Why it matters</h2>
        <p className="text-[16px] leading-[1.7] text-foreground/85">{r.whyItMatters}</p>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 font-serif text-xl text-ink">Key findings</h2>
        <ul className="space-y-3 list-disc list-outside pl-6 text-[15px] leading-relaxed text-foreground/85">
          {r.keyFindings.map((k, i) => <li key={i}>{k}</li>)}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 font-serif text-xl text-ink">Relevance to AI and sustainability</h2>
        <p className="text-[16px] leading-[1.7] text-foreground/85">{r.relevance}</p>
      </section>

      <section className="mb-8">
        <TopicList topics={r.topics} />
      </section>

      {(r.relatedIdeas.length > 0 || r.relatedConcepts.length > 0) && (
        <section className="mb-8 rounded-md border border-ink/10 bg-paper p-6 space-y-6">
          {r.relatedIdeas.length > 0 && (
            <div>
              <h2 className="mb-3 font-serif text-lg text-ink">Related ideas</h2>
              <ul className="space-y-2">
                {r.relatedIdeas.map((slug) => {
                  const i = ideaBySlug(slug);
                  if (!i) return null;
                  return (
                    <li key={slug}>
                      <Link to="/ideas/$slug" params={{ slug }} className="group block">
                        <p className="font-serif text-base text-ink group-hover:text-clay">{i.title}</p>
                        <p className="text-sm text-muted-foreground">{i.subtitle}</p>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {r.relatedConcepts.length > 0 && (
            <div>
              <h2 className="mb-3 font-serif text-lg text-ink">Related concepts</h2>
              <ul className="space-y-2">
                {r.relatedConcepts.map((slug) => {
                  const c = conceptBySlug(slug);
                  if (!c) return null;
                  return (
                    <li key={slug}>
                      <Link to="/concepts/$slug" params={{ slug }} className="group block">
                        <p className="font-serif text-base text-ink group-hover:text-clay">{c.name}</p>
                        <p className="text-sm text-muted-foreground">{c.oneLine}</p>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </section>
      )}

      <section className="mb-8">
        <a
          href={r.sourceUrl}
          target="_blank"
          rel="noopener"
          className="inline-flex items-center rounded-full border border-ink/30 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-ink hover:border-ink hover:bg-ink hover:text-paper transition-colors"
        >
          Read original source ↗
        </a>
      </section>

      <BriefingCTA variant="inline" />
    </article>
  );
}

function LibraryReport({ data }: { data: LibraryData }) {
  const { report, related } = data;
  const summary = report.summary_en || report.summary_tr;
  return (
    <article className="mx-auto max-w-3xl px-6 py-16 md:py-20">
      <nav aria-label="Breadcrumb" className="mb-8 text-[11px] uppercase tracking-[0.22em] text-muted-foreground flex gap-2">
        <Link to="/" className="hover:text-ink">Home</Link>
        <span aria-hidden>›</span>
        <Link to="/research" className="hover:text-ink">Source Notes</Link>
      </nav>
      <header className="mb-10">
        <p className="text-[10px] uppercase tracking-[0.24em] text-clay">
          Report{report.year ? ` · ${report.year}` : ""}
        </p>
        <h1 className="mt-4 font-serif text-3xl leading-tight text-ink md:text-4xl">{report.title}</h1>
        {report.organization && (
          <p className="mt-3 text-[13px] uppercase tracking-[0.14em] text-muted-foreground">{report.organization}</p>
        )}
      </header>
      <section className="mb-8">
        <h2 className="mb-3 font-serif text-xl text-ink">Summary</h2>
        {(summary || "A summary for this report is being prepared.").split(/\n{2,}/).map((p, i) => (
          <p key={i} className="mb-4 text-[16px] leading-[1.7] text-foreground/85">{p}</p>
        ))}
      </section>
      {report.topics?.length > 0 && (
        <section className="mb-8 flex flex-wrap gap-1.5">
          {report.topics.map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-[0.15em] text-ink/85 bg-ink/5 px-2 py-0.5">{t}</span>
          ))}
        </section>
      )}
      <section className="mb-10">
        <a
          href={report.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-full border border-ink/30 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-ink hover:border-ink hover:bg-ink hover:text-paper transition-colors"
        >
          Read original source ↗
        </a>
        <p className="mt-3 text-xs text-muted-foreground">
          Summary written for this site; the report itself belongs to its publisher.
        </p>
      </section>
      {related.length > 0 && (
        <section className="mb-10 border-t border-border pt-8">
          <h2 className="mb-4 font-serif text-lg text-ink">More reports in this field</h2>
          <ul className="space-y-3">
            {related.map((r) => (
              <li key={r.slug}>
                <Link to="/research/$slug" params={{ slug: r.slug }} className="font-serif text-lg hover:text-clay">
                  {r.title}
                </Link>
                {r.organization && (
                  <span className="ml-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    {r.organization}{r.year ? ` · ${r.year}` : ""}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      <BriefingCTA variant="inline" />
    </article>
  );
}
